import {useCallback, useMemo, useRef, useState, type MutableRefObject} from 'react'
import type {ExternalStoreAdapter} from '@assistant-ui/react'
import {newAgentSessionId} from '../agentSessionId'
import type {AgentWireEvent} from '../agentWireEvent'
import {
  BedrockSessionController,
  convertMessage,
  type ClearFileTagsFn,
  type ComposerSelectionFn,
  type SessionCancelFn,
  type SessionSendFn,
} from '../bedrockSessionController'
import {
  MAX_OPEN_CONVERSATION_TABS,
  pickLruEvictionCandidate,
  resolveConversationIdForWireEvent,
} from '../routeAgentWireEvent'
import {pickFilesChangedFields} from '../filesChanged'
import {titleFromUserMessage} from '../conversationListHelpers'
import type {Message} from './useChatMessageFormatting'
import {useChatMessageFormatting} from './useChatMessageFormatting'
import type {OmicsChatSendOptionsRef} from './useBedrockStoreAdapter'
import type {SendMessageOptions} from '../BedrockSocketProvider'
import type {ContextUsage} from '../omicsContextUsage'

export interface UseConversationSessionsParams {
  rawStreamModeRef?: MutableRefObject<boolean>
  sendOptionsRef: MutableRefObject<OmicsChatSendOptionsRef>
  composerSelectionRef: MutableRefObject<ComposerSelectionFn>
  clearFileTagsRef: MutableRefObject<ClearFileTagsFn | null>
  /** Called when the empty-state composer starts a turn in a freshly created draft tab. */
  onDraftFromEmptyState?: (conversationId: string) => void
  sendForSession: (
    conversationId: string,
    agentSessionId: string,
    message: string,
    messageId: string,
    options?: SendMessageOptions,
  ) => void
  cancelForSession: (
    messageId: string,
    conversationId: string,
    agentSessionId: string,
  ) => void
  /** A turn was sent; ``placeholderTitle`` names the conversation only if it has no title yet. */
  onConversationListTouch?: (id: string, placeholderTitle?: string) => void
}

export interface UseConversationSessionsReturn {
  activeConversationId: string | null
  openConversationIds: string[]
  activeController: BedrockSessionController | null
  activeAdapter: ExternalStoreAdapter<Message>
  activeThreadResyncKey: number
  activeThreadContextUsage: ContextUsage | null
  switchTo: (conversationId: string) => void
  ensureDraftSession: () => string
  /** Opens (or re-uses) a tab; `activate: false` adds it without taking focus. */
  openConversation: (
    conversationId: string,
    agentSessionId?: string,
    options?: {activate?: boolean},
  ) => void
  closeTab: (conversationId: string) => boolean
  getSession: (conversationId: string) => BedrockSessionController | undefined
  replaceActiveThreadMessages: (msgs: readonly Message[]) => void
  setActiveAgentSessionId: (id: string) => void
  setActiveThreadContextUsage: (usage: ContextUsage | null) => void
  dispatchWireEvent: (event: AgentWireEvent) => void
  sessionRunningById: ReadonlyMap<string, boolean>
  sessionTitleById: ReadonlyMap<string, string>
  bumpVersion: number
}


function createController(
  conversationId: string,
  agentSessionId: string,
  deps: ConstructorParameters<typeof BedrockSessionController>[2],
  onBump: () => void,
): BedrockSessionController {
  const controller = new BedrockSessionController(conversationId, agentSessionId, deps)
  controller.setListener(onBump)
  return controller
}

export function useConversationSessions({
  rawStreamModeRef,
  sendOptionsRef,
  composerSelectionRef,
  clearFileTagsRef,
  onDraftFromEmptyState,
  sendForSession,
  cancelForSession,
  onConversationListTouch,
}: UseConversationSessionsParams): UseConversationSessionsReturn {
  const formatting = useChatMessageFormatting()
  const [version, setVersion] = useState(0)
  const bump = useCallback(() => setVersion((v) => v + 1), [])

  const controllersRef = useRef(new Map<string, BedrockSessionController>())
  const messageIdToConversationRef = useRef(new Map<string, string>())
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const activeConversationIdRef = useRef<string | null>(null)
  activeConversationIdRef.current = activeConversationId
  const [openConversationIds, setOpenConversationIds] = useState<string[]>([])

  const sendFnRef = useRef<SessionSendFn | null>(null)
  const cancelFnRef = useRef<SessionCancelFn | null>(null)

  sendFnRef.current = (text, messageId, session, options) => {
    messageIdToConversationRef.current.set(messageId, session.conversationId)
    sendForSession(session.conversationId, session.agentSessionId, text, messageId, options)
    onConversationListTouch?.(session.conversationId, titleFromUserMessage(text))
  }

  cancelFnRef.current = (messageId, session) => {
    cancelForSession(messageId, session.conversationId, session.agentSessionId)
  }

  const controllerDeps = useMemo(
    () => ({
      processStreamingContent: formatting.processStreamingContent,
      createAgentResponseFromSteps: formatting.createAgentResponseFromSteps,
      createBotMessage: formatting.createBotMessage,
      rawStreamModeRef,
      sendOptionsRef,
      sendFnRef,
      cancelFnRef,
      composerSelectionRef,
      clearFileTagsRef,
    }),
    [
      formatting.processStreamingContent,
      formatting.createAgentResponseFromSteps,
      formatting.createBotMessage,
      rawStreamModeRef,
      sendOptionsRef,
      composerSelectionRef,
      clearFileTagsRef,
    ],
  )

  const getOrCreateController = useCallback(
    (conversationId: string, agentSessionId?: string): BedrockSessionController => {
      const existing = controllersRef.current.get(conversationId)
      if (existing) {
        if (agentSessionId && existing.agentSessionId !== agentSessionId) {
          existing.agentSessionId = agentSessionId
        }
        return existing
      }
      const controller = createController(
        conversationId,
        agentSessionId ?? newAgentSessionId(),
        controllerDeps,
        bump,
      )
      controllersRef.current.set(conversationId, controller)
      return controller
    },
    [controllerDeps, bump],
  )

  const evictIfNeeded = useCallback(
    (ids: string[]): string[] => {
      let next = ids
      if (next.length <= MAX_OPEN_CONVERSATION_TABS) {
        return next
      }
      const sessionsMeta = new Map(
        [...controllersRef.current.entries()].map(([id, c]) => [
          id,
          {isRunning: c.isRunning, lastActivityAt: c.lastActivityAt},
        ]),
      )
      const evict = pickLruEvictionCandidate(next, activeConversationId, sessionsMeta)
      if (evict) {
        next = next.filter((id) => id !== evict)
        controllersRef.current.delete(evict)
      }
      return next
    },
    [activeConversationId],
  )

  const buildRoutingIndex = useCallback(() => {
    const sessionByConversationId = new Map<string, unknown>()
    const sessionIdToConversationId = new Map<string, string>()
    for (const [id, controller] of controllersRef.current.entries()) {
      sessionByConversationId.set(id, controller)
      sessionIdToConversationId.set(controller.agentSessionId, id)
    }
    return {
      sessionByConversationId,
      messageIdToConversationId: messageIdToConversationRef.current,
      sessionIdToConversationId,
    }
  }, [])

  const openConversation = useCallback(
    (conversationId: string, agentSessionId?: string, options?: {activate?: boolean}) => {
      getOrCreateController(conversationId, agentSessionId)
      setOpenConversationIds((prev) => {
        if (prev.includes(conversationId)) {
          return prev
        }
        return evictIfNeeded([...prev, conversationId])
      })
      if (options?.activate !== false) {
        setActiveConversationId(conversationId)
      }
      bump()
    },
    [bump, evictIfNeeded, getOrCreateController],
  )

  const switchTo = useCallback(
    (conversationId: string) => {
      openConversation(conversationId)
    },
    [openConversation],
  )

  const ensureDraftSession = useCallback((): string => {
    const id = crypto.randomUUID()
    const agentSessionId = newAgentSessionId()
    getOrCreateController(id, agentSessionId)
    setOpenConversationIds((prev) => evictIfNeeded([...prev, id]))
    setActiveConversationId(id)
    bump()
    return id
  }, [bump, evictIfNeeded, getOrCreateController])

  const closeTab = useCallback(
    (conversationId: string): boolean => {
      const controller = controllersRef.current.get(conversationId)
      if (controller?.isRunning) {
        return false
      }
      controllersRef.current.delete(conversationId)
      setOpenConversationIds((prev) => {
        const remaining = prev.filter((id) => id !== conversationId)
        if (activeConversationId === conversationId) {
          setActiveConversationId(remaining.length > 0 ? remaining[remaining.length - 1] : null)
        }
        return remaining
      })
      bump()
      return true
    },
    [activeConversationId, bump],
  )

  const dispatchWireEvent = useCallback(
    (event: AgentWireEvent) => {
      let conversationId = resolveConversationIdForWireEvent(event, buildRoutingIndex())
      if (!conversationId && (event.type === 'error' || event.type === 'recursion_limit')) {
        conversationId = activeConversationIdRef.current
      }
      if (!conversationId) {
        return
      }
      const controller = getOrCreateController(conversationId)
      controller.touchActivity()

      switch (event.type) {
        case 'chunk':
          controller.onChunk(event.chunk, event.messageId, event.isComplete, event.metadata)
          break
        case 'step':
          controller.onStep(event.step)
          break
        case 'error':
          controller.onError(event.error, pickFilesChangedFields(event))
          break
        case 'recursion_limit':
          controller.onRecursionLimit({
            error: event.error,
            canResume: event.canResume,
            ...pickFilesChangedFields(event),
          })
          break
        case 'stream_cancelled_ack':
          controller.onStreamCancelledAck(event.messageId)
          break
        case 'run_detached':
          controller.onDetached(event.messageId)
          break
        case 'conversation_title':
          // The history list (the tab's title source) is updated by the socket provider.
          controller.title = event.title
          controller.notifyUpdate()
          break
        case 'context_usage':
          controller.threadContextUsage = event.contextUsage
          controller.notifyUpdate()
          break
        case 'conversation_id':
          break
        default:
          break
      }
    },
    [buildRoutingIndex, getOrCreateController],
  )

  const activeController =
    activeConversationId != null
      ? controllersRef.current.get(activeConversationId) ?? null
      : null

  const sessionRunningById = useMemo(() => {
    const map = new Map<string, boolean>()
    for (const [id, controller] of controllersRef.current.entries()) {
      map.set(id, controller.isRunning)
    }
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version drives refresh
  }, [version])

  const sessionTitleById = useMemo(() => {
    const map = new Map<string, string>()
    for (const [id, controller] of controllersRef.current.entries()) {
      map.set(id, controller.title)
    }
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version drives refresh
  }, [version])

  // Adapter used when no tab is active. A plain empty adapter with a no-op onNew would silently
  // drop a message sent in the zero-tab state; instead this auto-creates a tab (ensureDraftSession)
  // and forwards the message to its controller, so the query starts a new chat rather than doing
  // nothing.
  const emptyStateAdapter = useMemo<ExternalStoreAdapter<Message>>(
    () => ({
      messages: [],
      isRunning: false,
      convertMessage,
      onNew: async (message) => {
        const id = ensureDraftSession()
        onDraftFromEmptyState?.(id)
        await controllersRef.current.get(id)?.onNew(message)
      },
    }),
    [ensureDraftSession, onDraftFromEmptyState],
  )

  return {
    activeConversationId,
    openConversationIds,
    activeController,
    activeAdapter: activeController?.getAdapter() ?? emptyStateAdapter,
    activeThreadResyncKey: activeController?.threadResyncKey ?? 0,
    activeThreadContextUsage: activeController?.threadContextUsage ?? null,
    switchTo,
    ensureDraftSession,
    openConversation,
    closeTab,
    getSession: (id) => controllersRef.current.get(id),
    replaceActiveThreadMessages: (msgs) => {
      if (activeConversationId) {
        getOrCreateController(activeConversationId).replaceThreadMessages(msgs)
      }
    },
    setActiveAgentSessionId: (id) => {
      if (activeConversationId) {
        getOrCreateController(activeConversationId).agentSessionId = id
        bump()
      }
    },
    setActiveThreadContextUsage: (usage) => {
      if (activeConversationId) {
        const c = getOrCreateController(activeConversationId)
        c.threadContextUsage = usage
        c.notifyUpdate()
      }
    },
    dispatchWireEvent,
    sessionRunningById,
    sessionTitleById,
    bumpVersion: version,
  }
}
